import type { IGetWelcomeBonusResponse, IGetWelcomeBonusSpotsResponse } from '#api';
// import { LazyModalWelcomeOffer } from '#components';

const MAX_TOKENS = 15;
const TOTAL_CASH_AVAILABLE = 20;

const LS_WELCOME_OFFER_POPUP = 'welcome_offer_popup_shown';
const LS_WELCOME_SHOWN_VALUE = 'shown';

const WELCOME_OFFER_RELEASE_DATE = '2025-05-05 00:00:00';

export type TWelcomeBonusMessage = 'activated' | 'updated';

interface IWelcomeNotificationData {
  title: string;
  text: string;
}

export interface IWelcomeChestData {
  id: string;
  amount: string;
  claimed: boolean;
}

export const useWelcomeStore = defineStore('welcome', () => {
  const { $authApi } = useTypedNuxtApp();
  const { welcomeOfferEnabled } = useRuntimeConfig().public;

  const isEnabled = ref<boolean>(welcomeOfferEnabled);
  const isActive = ref<boolean | null>(null);
  const isArchived = ref<boolean | null>(null);
  const tokensCollected = ref(0);
  const notificationData = ref<IWelcomeNotificationData | null>(null);
  const chestList = ref<IWelcomeChestData[]>([]);

  const notificationsCount = computed(() => (notificationData === null ? 0 : 1));
  const localStorageKey = computed(() => `${LS_WELCOME_OFFER_POPUP}_${stores.user.getUserId()}`);
  const cashCollected = computed(() => {
    if (!chestList.value?.length) return 0;
    return chestList.value.reduce((acc, item) => acc + (item.claimed ? 1 : 0), 0);
  });
  const isCompleted = computed(() => cashCollected.value === TOTAL_CASH_AVAILABLE);
  const isOldUser = computed(() => {
    if (!stores.user.profileData) return false;
    const regDate = new Date(stores.user.profileData.createdAt);
    const oldDate = new Date(WELCOME_OFFER_RELEASE_DATE);
    return regDate.getTime() < oldDate.getTime();
  });
  const isAvailable = computed(() => {
    if (!isEnabled.value || isOldUser.value || isCompleted.value) return false;
    return isActive.value !== null;
  });

  // const shouldShowWelcomePopup = computed(() => {
  //   const lsValue = localStorage.getItem(localStorageKey.value);
  //   if (!isEnabled.value || lsValue === LS_WELCOME_SHOWN_VALUE || isOldUser.value) return false;
  //   return !isActive.value && !isCompleted.value;
  // });

  function setNotificationData(data: IWelcomeNotificationData) {
    notificationData.value = data;
  }

  function clearNotificationData() {
    notificationData.value = null;
  }

  async function getWelcomeBonusSpots() {
    try {
      const sessionKey = await stores.user.getIdToken();
      const { data, hasError } = await $authApi.post<IGetWelcomeBonusSpotsResponse>('/bonuses/getWelcomeBonusSpots', {
        sessionKey,
      });

      if (hasError || !data) {
        throw new Error('Failed to get getWelcomeBonusSpots data');
      }

      const {
        active, // it means a user has not deposited their first $10
        archived, // it means that the user is done with their bonuses and achievedSoFar is 20 (useless field tbh)
        spots, // means collected tokens
      } = data;

      isActive.value = active || false;
      isArchived.value = archived || false;
      tokensCollected.value = archived ? MAX_TOKENS : spots % MAX_TOKENS || 0;
    } catch (error) {
      isActive.value = false;
      console.warn('WO getWelcomeBonusSpots error:');
      console.warn(error?.response?.data || 'no spots, probably this is an old account');
    }
  }

  async function getWelcomeBonus() {
    try {
      const sessionKey = await stores.user.getIdToken();
      const { data, hasError } = await $authApi.post<IGetWelcomeBonusResponse>('/bonuses/getWelcomeBonus', {
        sessionKey,
      });

      if (hasError || !data) {
        throw new Error('Failed to get getWelcomeBonusSpots data');
      }

      chestList.value = data;
    } catch (error) {
      console.warn('WO getWelcomeBonus error:');
      console.warn(error?.response?.data);
    }
  }

  async function syncWelcomeBonus(message: TWelcomeBonusMessage) {
    let title: string;
    let text: string;

    await Promise.all([getWelcomeBonusSpots(), getWelcomeBonus()]);

    if (message === 'activated') {
      title = '🎁 You’ve got an offer!';
      text = 'Click here to find out more!';
    } else if (message === 'updated') {
      if (tokensCollected.value === 1) {
        title = '🔓 You just got your first token!';
        text = 'Fill the rest to claim your reward.';
      } else if (tokensCollected.value === MAX_TOKENS) {
        title = '🎁 Congratulations!';
        text = 'Click here to collect your reward.';
      } else {
        title = '🔓 You just got your token!';
        text = 'Fill the rest to claim your reward.';
      }
    }

    title && setNotificationData({ title, text });
  }

  // function checkAndShowWelcomeModal() {
  //   if (!shouldShowWelcomePopup.value) return;
  //   stores.modals.open('welcome-offer', LazyModalWelcomeOffer);
  // }

  function setWelcomeModalShown() {
    localStorage.setItem(localStorageKey.value, LS_WELCOME_SHOWN_VALUE);
  }

  return {
    totalTokens: MAX_TOKENS,
    totalCashAvailable: TOTAL_CASH_AVAILABLE,

    isEnabled,
    isAvailable,
    isActive,
    isArchived,
    isCompleted,

    tokensCollected,
    cashCollected,
    chestList,

    notificationData,
    notificationsCount,

    // checkAndShowWelcomeModal,
    setWelcomeModalShown,
    setNotificationData,
    clearNotificationData,
    getWelcomeBonusSpots,
    getWelcomeBonus,
    syncWelcomeBonus,
  };
});
